import React, { useCallback, useMemo, useState } from "react";
import {
  View,
  FlatList,
  Pressable,
  ActivityIndicator,
  StyleSheet,
} from "react-native";
import { useFocusEffect } from "@react-navigation/native";
import { showApiError } from "@components/ds/showApiError";
import { t } from "@constants/localization";
import { getDsRoutingDbList } from "@services/api/dsRouting";
import RoutingHeader from "../components/RoutingHeader";
import RoutingSearch from "../components/RoutingSearch";
import { useRoutingSearch } from "../useRoutingSearch";
import { filterDbs, routingErrorKey } from "../helpers";
import { C, RText, Tag, ChevronRight } from "../theme";

const PATH = "DsRouting/byDb";

// Registered as `DsRoutingDbList`; opened from the hub's "By delivery boy"
// card. One row per DB with a TP today: counts and a "N ready for OFD" pill.
// A row opens DsRoutingDbTps. Reloads on focus (back from a Mark OFD).
const DsRoutingDbList = ({ navigation }) => {
  const [dbs, setDbs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  // Search by delivery boy name or the phone's shown digits.
  const search = useRoutingSearch();
  const shown = useMemo(
    () => filterDbs(dbs, search.query),
    [dbs, search.query],
  );

  const reload = useCallback(async ({ refreshing = false } = {}) => {
    try {
      if (refreshing) setIsRefreshing(true);
      else setIsLoading(true);
      const response = await getDsRoutingDbList();
      setDbs(response?.data?.data?.dbs || []);
    } catch (error) {
      showApiError(error, routingErrorKey);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      reload();
    }, [reload]),
  );

  const renderItem = ({ item }) => (
    <Pressable
      testID={`ds-routing-db-${item.cp_db_id}`}
      onPress={() =>
        navigation.navigate("DsRoutingDbTps", {
          cp_db_id: item.cp_db_id,
          name: item.name,
        })
      }
      style={styles.card}
    >
      <View style={styles.info}>
        <RText size={16} weight={700} numberOfLines={1}>
          {item.name || String(item.cp_db_id)}
        </RText>
        {item.phone_masked ? (
          <RText size={12} weight={500} color={C.muted}>
            {item.phone_masked}
          </RText>
        ) : null}
        <RText size={12.5} weight={600} color={C.muted}>
          {t(`${PATH}/counts`, {
            total: String(item.total ?? 0),
            picked: String(item.picked ?? 0),
            ofd: String(item.ofd ?? 0),
          })}
        </RText>
        {item.ofd_ready > 0 ? (
          <Tag
            label={t(`${PATH}/ready`, { count: String(item.ofd_ready) })}
            bg={C.greenBg}
            color={C.green}
            size={10}
            style={styles.ready}
          />
        ) : null}
      </View>
      <ChevronRight />
    </Pressable>
  );

  return (
    <View style={styles.screen}>
      <RoutingHeader title={t(`${PATH}/title`)} navigation={navigation} />
      <RoutingSearch
        testID="ds-routing-db-list-search"
        value={search.text}
        onChangeText={search.setText}
        placeholder={t(`${PATH}/search`)}
      />
      {isLoading && dbs.length === 0 ? (
        <ActivityIndicator size="large" color={C.red} style={styles.loader} />
      ) : (
        <FlatList
          testID="ds-routing-db-list"
          showsVerticalScrollIndicator={false}
          data={shown}
          keyExtractor={(item) => String(item.cp_db_id)}
          renderItem={renderItem}
          refreshing={isRefreshing}
          onRefresh={() => reload({ refreshing: true })}
          contentContainerStyle={styles.listBody}
          ListHeaderComponent={
            <RText size={12} weight={500} color={C.muted} style={styles.helper}>
              {t(`${PATH}/helper`)}
            </RText>
          }
          ListEmptyComponent={
            isLoading ? null : (
              <RText
                size={14}
                weight={500}
                color={C.muted}
                style={styles.empty}
              >
                {t(
                  search.query ? "DsRouting/dbSheet/noMatch" : `${PATH}/empty`,
                )}
              </RText>
            )
          }
        />
      )}
    </View>
  );
};

export default DsRoutingDbList;

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: C.page },
  loader: { flex: 1 },
  listBody: { paddingTop: 12, paddingBottom: 24 },
  helper: { paddingHorizontal: 16, paddingBottom: 12, lineHeight: 18 },
  card: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    backgroundColor: C.surface,
    borderWidth: 1,
    borderColor: C.border,
    borderRadius: 12,
    padding: 14,
    marginHorizontal: 16,
    marginBottom: 12,
  },
  info: { flex: 1, gap: 4 },
  ready: { marginTop: 3, paddingVertical: 5, paddingHorizontal: 11 },
  empty: { textAlign: "center", marginTop: 60 },
});
